/** @jsx jsx */
import { jsx } from "theme-ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { navigate } from "gatsby";
import Iframe from "react-iframe";
import { StickyProvider } from "contexts/app/app.provider";
import SEO from "components/seo";
import CatalogueLayout from "components/auxiliary/catalogue-layout";
import ChooseCarModel from "components/catalogue/ChooseCarModel";
import { Box, Button } from "theme-ui";
import { useCatalogueVehicleSearch } from "hooks/use-catalogue-vehicle-search";
import { useRequireWorkshopSession } from "hooks/use-require-workshop-session";
import {
  clearLatestCataloguePartId,
  getLatestCataloguePartId,
  LATEST_CATALOGUE_PART_ID_KEY,
} from "utils/cataloguePartStorage";

const ORIPARTS_NAVBAR_HEIGHT_PX = 56;

export default function CataloguePage() {
  const { ready, bootstrapping } = useRequireWorkshopSession("/catalogue/");
  const [catalogueSourceUri, setCatalogueSourceUri] = useState("");
  const [partId, setPartId] = useState(null);
  const [PartDetailsModal, setPartDetailsModal] = useState(null);
  const lastShownPartIdRef = useRef(null);

  const handleCatalogueReady = useCallback((iframeUrl) => {
    setCatalogueSourceUri(iframeUrl);
  }, []);

  const {
    vehicleSearchValues,
    isVehicleModalOpen,
    isVehicleSearchValuesValid,
    errorMessage,
    isLpnSearching,
    openVehicleModal,
    closeVehicleModal,
    handleVehicleSearchValueChange,
    handleVehicleSearchConfirm,
    handleVehicleSearchError,
    handleLpnSearch,
  } = useCatalogueVehicleSearch(handleCatalogueReady);

  const handleVehicleModalClose = useCallback(() => {
    if (catalogueSourceUri) {
      closeVehicleModal();
      return;
    }

    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }

    navigate("/");
  }, [catalogueSourceUri, closeVehicleModal]);

  useEffect(() => {
    import("components/PartDetailsModal").then((module) => {
      setPartDetailsModal(() => module.default);
    });
  }, []);

  useEffect(() => {
    clearLatestCataloguePartId();
    return () => {
      clearLatestCataloguePartId();
    };
  }, []);

  const handleClosePartModal = () => {
    clearLatestCataloguePartId();
    lastShownPartIdRef.current = null;
    setPartId(null);
  };

  useEffect(() => {
    const checkForNewPart = () => {
      const latestPartId = getLatestCataloguePartId();

      if (!latestPartId || latestPartId === lastShownPartIdRef.current) {
        return;
      }

      lastShownPartIdRef.current = latestPartId;
      setPartId(latestPartId);
    };

    const handleStorage = (event) => {
      if (event.key === LATEST_CATALOGUE_PART_ID_KEY && event.newValue) {
        checkForNewPart();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForNewPart();
      }
    };

    checkForNewPart();
    window.addEventListener("focus", checkForNewPart);
    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", checkForNewPart);
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  if (bootstrapping || !ready) {
    return (
      <StickyProvider>
        <CatalogueLayout>
          <SEO
            title="Garaaz - Parts Catalogue"
            description="Browse automobile spare parts catalogue"
            keywords="garaaz, spare parts, catalogue, automobile"
          />
          <Box
            sx={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "muted",
              fontSize: 2,
            }}
          >
            {bootstrapping ? "Verifying sign-in…" : "Redirecting to login…"}
          </Box>
        </CatalogueLayout>
      </StickyProvider>
    );
  }

  return (
    <StickyProvider>
      <CatalogueLayout>
        <SEO
          title="Garaaz - Parts Catalogue"
          description="Browse automobile spare parts catalogue"
          keywords="garaaz, spare parts, catalogue, automobile"
        />

        {catalogueSourceUri && (
          <Box
            sx={{
              position: "absolute",
              top: "12px",
              right: "16px",
              zIndex: 2,
            }}
          >
            <Button
              sx={{
                marginTop: "50px",
                marginRight: "150px",
                minHeight: "36px",
                fontSize: 1,
                px: 3,
                bg: "background",
                border: "1px solid",
                borderColor: "border_color",
                color: "text",
              }}
              onClick={() => {
                openVehicleModal();
              }}
            >
              Change vehicle
            </Button>
          </Box>
        )}

        <Box
          as="section"
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            backgroundColor: "background",
          }}
        >
          {catalogueSourceUri ? (
            <Iframe
              url={catalogueSourceUri}
              width="100%"
              height="100%"
              id="garaaz-catalogue-iframe"
              className="garaaz-catalogue-iframe"
              display="block"
              position="relative"
              frameBorder={0}
              styles={{
                border: 0,
                display: "block",
                width: "100%",
                height: `calc(100% + ${ORIPARTS_NAVBAR_HEIGHT_PX}px)`,
                marginTop: `-${ORIPARTS_NAVBAR_HEIGHT_PX}px`,
              }}
              allowFullScreen
            />
          ) : (
            <Box
              sx={{
                minHeight: "60vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "muted",
                fontSize: 2,
              }}
            >
              Select a vehicle to browse the OEM catalogue.
            </Box>
          )}
        </Box>

        <ChooseCarModel
          isVisible={isVehicleModalOpen}
          onClose={handleVehicleModalClose}
          onConfirm={handleVehicleSearchConfirm}
          selectedValues={vehicleSearchValues}
          onValueChange={handleVehicleSearchValueChange}
          onError={handleVehicleSearchError}
          errorMessage={errorMessage}
          showCancelButton={false}
          confirmButtonText="View OEM Catalogue"
          slideUpPanelTitle="Select Vehicle"
          isConfirmButtonDisabled={!isVehicleSearchValuesValid}
          onLpnSearch={handleLpnSearch}
          allowClose
          isLpnSearching={isLpnSearching}
        />

        {PartDetailsModal && (
          <PartDetailsModal
            partId={partId}
            isOpen={Boolean(partId)}
            onClose={handleClosePartModal}
          />
        )}
      </CatalogueLayout>
    </StickyProvider>
  );
}
